import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign,
  Plus,
  Send,
  ExternalLink,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  CreditCard,
  FileText,
  RefreshCw,
} from 'lucide-react';
import api, { Engagement, BillingSummary } from '../services/api';

interface BillingSectionProps {
  clientId: string;
}

const STATUS_BADGES: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  unbilled: { label: 'Unbilled', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', icon: <Clock className="w-3.5 h-3.5" /> },
  invoiced: { label: 'Invoiced', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300', icon: <FileText className="w-3.5 h-3.5" /> },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300', icon: <CheckCircle className="w-3.5 h-3.5" /> },
  subscription_active: { label: 'Subscription Active', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300', icon: <CreditCard className="w-3.5 h-3.5" /> },
  subscription_past_due: { label: 'Past Due', color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300', icon: <AlertTriangle className="w-3.5 h-3.5" /> },
  subscription_cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300', icon: <XCircle className="w-3.5 h-3.5" /> },
};

const ENGAGEMENT_STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  sent: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
  paid: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  refunded: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
};

const BillingSection: React.FC<BillingSectionProps> = ({ clientId }) => {
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [editingContract, setEditingContract] = useState(false);
  const [contractInput, setContractInput] = useState('');
  const [stripeMode, setStripeMode] = useState<string>('test');
  const [hasLiveKeys, setHasLiveKeys] = useState(false);
  const [modeLoading, setModeLoading] = useState(false);

  // Create engagement form state
  const [newEngagement, setNewEngagement] = useState({
    engagement_type: 'initial' as 'initial' | 'cruising',
    amount_dollars: '',
    description: '',
  });

  const loadBilling = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getBillingSummary(clientId);
      setSummary(data);
    } catch (err) {
      console.error('Failed to load billing summary:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadBilling();
    api.getStripeSettings().then(s => { setStripeMode(s.mode); setHasLiveKeys(s.has_live_keys); }).catch(() => {});
  }, [loadBilling]);

  const handleCreateEngagement = async (e: React.FormEvent) => {
    e.preventDefault();
    const amountCents = Math.round(parseFloat(newEngagement.amount_dollars) * 100);
    if (isNaN(amountCents) || amountCents <= 0) return;

    try {
      setActionLoading('create');
      await api.createEngagement(clientId, {
        engagement_type: newEngagement.engagement_type,
        amount_cents: amountCents,
        description: newEngagement.description,
      });
      setShowCreateForm(false);
      setNewEngagement({ engagement_type: 'initial', amount_dollars: '', description: '' });
      await loadBilling();
    } catch (err) {
      console.error('Failed to create engagement:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendInvoice = async (engagement: Engagement, testEmail?: string) => {
    try {
      setActionLoading(`invoice-${engagement.id}`);
      const result = await api.sendEngagementInvoice(clientId, engagement.id, testEmail);
      await loadBilling();
      if (testEmail) {
        alert(`Test invoice sent to ${result.sent_to}`);
      }
    } catch (err) {
      console.error('Failed to send invoice:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateCheckout = async (engagement: Engagement) => {
    try {
      setActionLoading(`checkout-${engagement.id}`);
      const result = await api.createCheckoutSession(clientId, engagement.id, {
        success_url: window.location.href,
        cancel_url: window.location.href,
      });
      if (result.checkout_url) {
        window.open(result.checkout_url, '_blank');
      }
      await loadBilling();
    } catch (err) {
      console.error('Failed to create checkout:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleStripeMode = async () => {
    const newMode = stripeMode === 'live' ? 'test' : 'live';
    if (newMode === 'live' && !window.confirm('Switch to LIVE mode? Real charges will be created.')) return;
    try {
      setModeLoading(true);
      const result = await api.setStripeMode(newMode);
      setStripeMode(result.mode);
    } catch (err) {
      console.error('Failed to switch Stripe mode:', err);
    } finally {
      setModeLoading(false);
    }
  };

  const handleSaveContract = async () => {
    const amount = parseFloat(contractInput);
    if (isNaN(amount) || amount < 0) return;
    try {
      setActionLoading('contract');
      await api.updateClient(clientId, { contract_amount: amount.toFixed(2) } as any);
      await loadBilling();
      setEditingContract(false);
    } catch (err) {
      console.error('Failed to update contract amount:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Billing</h3>
        </div>
        <div className="flex justify-center py-8">
          <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (!summary) return null;

  const billingBadge = STATUS_BADGES[summary.billing_status] || STATUS_BADGES.unbilled;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Billing</h3>
          <button
            onClick={handleToggleStripeMode}
            disabled={modeLoading || (stripeMode === 'test' && !hasLiveKeys)}
            title={stripeMode === 'test' && !hasLiveKeys ? 'No live keys configured' : `Click to switch to ${stripeMode === 'live' ? 'test' : 'live'} mode`}
            className={`px-2 py-0.5 rounded text-xs font-medium cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${stripeMode === 'live' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800' : 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-800'}`}
          >
            {modeLoading ? '...' : stripeMode === 'live' ? 'LIVE' : 'TEST'}
          </button>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Engagement
        </button>
      </div>

      {/* Status row */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${billingBadge.color}`}>
          {billingBadge.icon}
          {billingBadge.label}
        </span>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          Paid: <span className="font-medium text-gray-900 dark:text-white">${parseFloat(summary.total_spent).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
        </span>
        {editingContract ? (
          <span className="inline-flex items-center gap-1">
            <span className="text-sm text-gray-500">Contract: $</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={contractInput}
              onChange={e => setContractInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveContract(); if (e.key === 'Escape') setEditingContract(false); }}
              className="w-24 px-1.5 py-0.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              autoFocus
            />
            <button onClick={handleSaveContract} disabled={actionLoading === 'contract'} className="text-xs text-blue-600 hover:text-blue-700">Save</button>
            <button onClick={() => setEditingContract(false)} className="text-xs text-gray-400 hover:text-gray-500">Cancel</button>
          </span>
        ) : (
          <button
            onClick={() => { setContractInput(summary.contract_amount); setEditingContract(true); }}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            title="Click to edit contract amount"
          >
            Contract: <span className="font-medium text-gray-900 dark:text-white">{parseFloat(summary.contract_amount) > 0 ? `$${parseFloat(summary.contract_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : 'Set amount'}</span>
          </button>
        )}
        {parseFloat(summary.contract_amount) > 0 && !editingContract && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Remaining: <span className={`font-medium ${parseFloat(summary.balance_due) > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>${parseFloat(summary.balance_due).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
          </span>
        )}
        {summary.stripe_subscription_id && (
          <span className="text-xs text-gray-400">Sub: {summary.stripe_subscription_id}</span>
        )}
      </div>

      {/* Create engagement form */}
      {showCreateForm && (
        <form onSubmit={handleCreateEngagement} className="mb-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
              <select
                value={newEngagement.engagement_type}
                onChange={e => setNewEngagement(prev => ({ ...prev, engagement_type: e.target.value as 'initial' | 'cruising' }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="initial">Initial</option>
                <option value="cruising">Cruising</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Amount ($)</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                required
                placeholder="0.00"
                value={newEngagement.amount_dollars}
                onChange={e => setNewEngagement(prev => ({ ...prev, amount_dollars: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <input
                type="text"
                placeholder="Engagement description"
                value={newEngagement.description}
                onChange={e => setNewEngagement(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={actionLoading === 'create'}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
            >
              {actionLoading === 'create' ? 'Creating...' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreateForm(false)}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Engagements list */}
      {summary.engagements.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">No engagements yet</p>
      ) : (
        <div className="space-y-2">
          {summary.engagements.map(eng => (
            <div
              key={eng.id}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      ${eng.amount_dollars.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ENGAGEMENT_STATUS_COLORS[eng.status] || ''}`}>
                      {eng.status}
                    </span>
                    <span className="text-xs text-gray-400 capitalize">{eng.engagement_type}</span>
                  </div>
                  {eng.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{eng.description}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-0.5">
                    {new Date(eng.created_at).toLocaleDateString()}
                    {eng.paid_at && ` | Paid ${new Date(eng.paid_at).toLocaleDateString()}`}
                  </p>
                </div>
              </div>

              {/* Action buttons for draft/sent engagements */}
              {(eng.status === 'draft' || eng.status === 'sent') && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      if (stripeMode === 'live') {
                        const confirmed = window.confirm(
                          `Send LIVE invoice for $${eng.amount_dollars.toLocaleString('en-US', { minimumFractionDigits: 2 })} to the client?\n\nThis will create a real Stripe invoice and email the client.`
                        );
                        if (!confirmed) return;
                      }
                      handleSendInvoice(eng);
                    }}
                    disabled={actionLoading === `invoice-${eng.id}`}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                    title="Send invoice to client"
                  >
                    <Send className="w-3 h-3" />
                    Invoice
                  </button>
                  <button
                    onClick={() => {
                      const email = prompt('Send test invoice to:', 'flo@digimint.co');
                      if (email) handleSendInvoice(eng, email);
                    }}
                    disabled={actionLoading === `invoice-${eng.id}`}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded hover:bg-amber-100 dark:hover:bg-amber-900/50 disabled:opacity-50"
                    title="Send invoice to your email for testing"
                  >
                    <Send className="w-3 h-3" />
                    Test
                  </button>
                  <button
                    onClick={() => handleCreateCheckout(eng)}
                    disabled={actionLoading === `checkout-${eng.id}`}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded disabled:opacity-50"
                    title="Create Checkout Link"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Checkout
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BillingSection;
